import React, { memo, useEffect, useRef } from 'react';
import { FaExpandAlt } from 'react-icons/fa';

interface FullScreenPillProps {
  /** Enter full screen */
  onClick: () => void;

  /**
   * Set by the overlay when the viewer leaves full screen, so focus returns to
   * the pill that took them there. The pill clears it once it has taken focus.
   */
  returnFocusRef?: React.MutableRefObject<boolean>;
}

/**
 * The "Full screen" entry under the corner tiles (entry option B, epic D6).
 *
 * A real button with an icon *and* visible text, in the row with the `+N`
 * chip; nothing is added to the bottom bar. It opts back into pointer events
 * because the tile container lets clicks through to the map.
 */
const FullScreenPillComponent: React.FC<FullScreenPillProps> = ({ onClick, returnFocusRef }) => {
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (returnFocusRef?.current) {
      returnFocusRef.current = false;
      buttonRef.current?.focus();
    }
  }, [returnFocusRef]);

  return (
    <button
      ref={buttonRef}
      type="button"
      onClick={onClick}
      className="pointer-events-auto inline-flex h-9 items-center gap-2 whitespace-nowrap rounded-full border border-game-medium bg-game-dark/95 px-3.5 text-sm font-semibold text-game-cream shadow-lg shadow-game-navy/40 transition-colors duration-200 hover:bg-game-dark hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-game-golden focus-visible:ring-offset-2 focus-visible:ring-offset-game-navy"
    >
      <FaExpandAlt aria-hidden="true" className="text-sm" />
      <span>Full screen</span>
    </button>
  );
};

export const FullScreenPill = memo(FullScreenPillComponent);
FullScreenPill.displayName = 'FullScreenPill';
